import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import Modal from 'react-modal';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import getCameraPlugin from '../utils/cameraPlugin';
import '../styles/seeContent.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Checkbox, List, ListItem, ListItemText, TextField, CircularProgress } from '@mui/material';

Modal.setAppElement('#root');

export default function SeeContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { photo, video } = location.state || {};

  const CameraPlugin = getCameraPlugin();

  const videoRef = useRef(null);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [savingToTrippin, setSavingToTrippin] = useState(false);
  const [flashing, setFlashing] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingUploadData, setPendingUploadData] = useState(null);

  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [closeFriends, setCloseFriends] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const forcePlay = async () => {
      if (videoRef.current && video) {
        try {
          videoRef.current.pause();
          videoRef.current.load();
  
          await new Promise((resolve) => setTimeout(resolve, 100));
  
          await videoRef.current.play();
          console.log("✅ Video playback started");
        } catch (err) {
          console.warn("⚠️ Video play failed:", err);
        }
      }
    };
  
    forcePlay();
  }, [video]);

  useEffect(() => {
    const fetchCloseUsers = async () => {
      try {
        const response = await axiosInstance.get('/flashes/close-users');
        setCloseFriends(response.data.users || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    fetchCloseUsers();
  }, []);

  const handleDiscard = async () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }

    if (video?.startsWith('blob:')) {
      URL.revokeObjectURL(video);
    }

    if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      navigate(-1);

      const result = await CameraPlugin.openCamera({ animated: false });
      
      if (!result || !result.fileUri) {
        console.error('Error: No fileUri received from android or iOS.');
        return;
      }

      if (result.isVideo) {
        navigate('/see-content', { state: { video: result.fileUri } });
      }
      else {
        navigate('/see-content', { state: { photo: result.fileUri } });
      }
    }
    else {
      navigate(-1);
    }
  };

  const handleDownload = async () => {
    if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      try {
        const isVideo = !!video;
        let filePath = isVideo ? video : photo;

        if (filePath.startsWith("data:video/mp4;base64,")) {
          console.log("📂 Converting Base64 video to file...");
    
          const base64Data = filePath.split(",")[1];
    
          const savedFile = await Filesystem.writeFile({
            path: "saved_video.mp4",
            data: base64Data,
            directory: Directory.Documents,
            recursive: true,
          });
    
          filePath = savedFile.uri;
    
          console.log("✅ Base64 video saved to:", filePath);
        }
    
        console.log("📂 Final File Path Sent to Native:", filePath);
    
        await CameraPlugin.saveToGallery({
          filePath,
          isVideo,
          stripAudio: !isAudioOn,
        });
      } catch (error) {
        console.error(error);
      }
    }
    else {
      try {
        const link = document.createElement('a');
      
        if (photo) {
          link.href = photo;
          link.download = 'captured_photo.png';
        }
        else if (video) {
          if (isAudioOn) {
            const response = await fetch(video);
            if (!response.ok) {
              throw new Error('Failed to fetch video for download');
            }
      
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
      
            link.href = blobUrl;
            link.download = 'captured_video.mp4';
      
            link.addEventListener('click', () => {
              setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
            });
          }
          else {
            const videoResponse = await fetch(video);
            if (!videoResponse.ok) {
              throw new Error('Failed to fetch video for stripping audio');
            }
      
            const videoBlob = await videoResponse.blob();
            const formData = new FormData();
            formData.append('file', videoBlob, 'video.mp4');
      
            const response = await axiosInstance.post('/strip-audio', formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              responseType: 'blob',
            });
      
            const processedBlob = response.data;
            const blobUrl = URL.createObjectURL(processedBlob);
      
            link.href = blobUrl;
            link.download = 'captured_video_no_audio.mp4';
      
            link.addEventListener('click', () => {
              setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
            });
          }
        }
      
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error during download:', error);
        alert('An error occurred while downloading the file.');
      }
    }
  };

  const toggleAudio = () => {
    setIsAudioOn(prev => !prev);
  };

  const handleSaveToTrippin = async () => {
    setSavingToTrippin(true);
    try {
      const isVideo = !!video;

      const fileBlob = await fetch(isVideo ? video : photo).then((res) => res.blob());

      const formData = new FormData();
  
      const coordinates = await Geolocation.getCurrentPosition();
      const longitude = coordinates.coords.longitude;
      const latitude = coordinates.coords.latitude;
  
      formData.append('file', fileBlob);
      formData.append('longitude', longitude);
      formData.append('latitude', latitude);
      if (!isAudioOn) formData.append('stripAudio', 'true');
  
      const response = await axiosInstance.post('/trippin/save', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      const data = response.data;
  
      console.log('File uploaded successfully:', data);
  
      if (data.askToStartTrippin) {
        setPendingUploadData({ formData });
        setIsModalOpen(true);
      }
      else {
        alert('Image saved to Trippin!');
      }
    } catch (err) {
      console.error('Error while saving image to Trippin:', err);
      alert('An error occurred while saving the image.');
    } finally {
      setSavingToTrippin(false);
    }
  };
  
  const confirmStartTrippin = async () => {
    try {
      if (pendingUploadData) {
        const { formData } = pendingUploadData;
  
        // Add the custom header for starting Trippin
        const response = await axiosInstance.post('/trippin/save', formData, {
          headers: {
            'Start-Trippin': 'true', // Custom header
            'Content-Type': 'multipart/form-data',
          },
        });
  
        if (response.status === 200) {
          alert('Image saved and Trippin started!');
        }
        else {
          alert('Failed to start Trippin.');
        }
      }
    } catch (err) {
      console.error('Error while starting Trippin:', err);
      alert('An error occurred.');
    } finally {
      setIsModalOpen(false);
      setPendingUploadData(null);
    }
  };
  
  const cancelStartTrippin = () => {
    setIsModalOpen(false);
    setPendingUploadData(null);
  };

  const handleFlash = async (isCloseFriends) => {
    setFlashing(true);
    try {
      const isVideo = !!video;

      const fileBlob = await fetch(isVideo ? video : photo).then((res) => res.blob());

      const formData = new FormData();
  
      formData.append('file', fileBlob);
  
      const coordinates = await Geolocation.getCurrentPosition();
      formData.append('latitude', coordinates.coords.latitude);
      formData.append('longitude', coordinates.coords.longitude);
  
      formData.append('isCloseFriends', isCloseFriends);

      formData.append('tags', JSON.stringify(selectedUsers));

      if (!isAudioOn) formData.append('stripAudio', 'true');
  
      const response = await axiosInstance.post('/flash/post', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      if (response.ok) {
        const data = await response.data;
        alert(data.message || 'Flash saved successfully!');
      }
      else {
        const error = await response.data;
        alert(error.message || 'Failed to save Flash.');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while saving the Flash.');
    } finally {
      setFlashing(false);
    }
  };
  
  const handleFlashIt = () => setIsTagDialogOpen(true);

  const handleSearch = async (term) => {
    if (!term) {
      setFilteredUsers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get('/flashes/search-users', {
        params: { query: term },
      });
      setFilteredUsers(response.data.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const confirmTags = (isCloseFriends) => {
    setIsTagDialogOpen(false);
    handleFlash(isCloseFriends);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const displayedUsers = searchTerm ? filteredUsers : closeFriends;

  return (
    <div className="see-content-container">
      {(photo || video) ? (
        <>
          <div className="top-right-icons">
            <div className="icon-wrapper" onClick={handleDiscard}>
              <img
                src='assets/icons/discard.png'
                alt="Discard"
                className="icon-button"
              />
            </div>
            <div className="icon-wrapper" onClick={handleDownload}>
              <img
                src='assets/icons/download.png'
                alt="Download"
                className="icon-button"
              />
            </div>
            {video && (
              <div className="icon-wrapper" onClick={toggleAudio}>
                <img
                  src={isAudioOn ? 'assets/icons/audio_on.png' : 'assets/icons/audio_off.png'}
                  alt="Audio"
                  className="icon-button"
                />
              </div>
            )}
          </div>
          {photo ? (
            <img src={photo} alt="Captured" className="captured-photo" />
          ) : (
            <video
              ref={videoRef}
              loop
              playsInline
              autoPlay
              muted={!isAudioOn}
              className="captured-video"
              style={{ marginBottom: "20px", width: "100%" }}
            >
              <source src={video} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
          <div className="button-container">
            <button
              onClick={handleSaveToTrippin}
              className="content-button"
              disabled={savingToTrippin || flashing}
            >
              {savingToTrippin ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save to trippin'}
            </button>
            <button
              onClick={handleFlashIt}
              className="content-button"
              disabled={savingToTrippin || flashing}
            >
              {flashing ? <CircularProgress size={20} sx={{ color: 'black' }} /> : 'Flash it'}
            </button>
          </div>

          <Modal isOpen={isModalOpen} onRequestClose={cancelStartTrippin}>
            <h2>Start a new Trippin?</h2>
            <p>You don't have an active <b>trippin</b>.<br />Would you like to start a new one?</p>
            <div>
              <button onClick={confirmStartTrippin}>Yes</button>
              <button onClick={cancelStartTrippin}>Cancel</button>
            </div>
          </Modal>

          <Dialog
            open={isTagDialogOpen}
            onClose={() => setIsTagDialogOpen(false)}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: "12px",
                  padding: "14px",
                  maxWidth: "400px",
                  width: "100%",
                },
              },
            }}
          >
            <DialogTitle>Tag users</DialogTitle>
            <DialogContent>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              />
              <List sx={{ maxHeight: "250px", overflowY: "auto", mt: 1 }}>
                {displayedUsers.map((user) => (
                  <ListItem
                    key={user.user_id}
                    button
                    onClick={() => toggleUserSelection(user.user_id)}
                    sx={{
                      padding: 0,
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.05)" },
                    }}
                  >
                    <Checkbox checked={selectedUsers.includes(user.user_id)} />
                    <ListItemText primary={user.username} />
                  </ListItem>
                ))}
                
                {loading && (
                  <ListItem sx={{ display: "flex", justifyContent: "center", padding: "12px" }}>
                    <CircularProgress size={24} sx={{ color: "#888" }} />
                  </ListItem>
                )}
              </List>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-evenly" }}>
              <Button
                onClick={() => confirmTags(false)}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  padding: "5px 16px",
                  backgroundColor: "#2c3e50",
                  color: "white",
                  borderRadius: "5px",
                }}
              >
                Flash
              </Button>
              <Button
                onClick={() => confirmTags(true)}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  padding: "5px 16px",
                  backgroundColor: "#2c3e50",
                  color: "white",
                  borderRadius: "5px",
                }}
              >
                Flash close
              </Button>
              <Button
                onClick={() => setIsTagDialogOpen(false)}
                sx={{ textTransform: "none", fontSize: "16px", color: "#2c3e50" }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <>
          <button className="close-button" onClick={handleDiscard}>
            <img src="assets/icons/close.png" alt="Close" />
          </button>
          <p style={{ color: "white", fontSize: "large" }}>No photo or video to display</p>
        </>
      )}
    </div>
  );
}